'use client'

import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@/lib/pwa-storage'
import { utcDayOf } from '@/server/domain/santa-chat/public'

/**
 * El token firmado vive en `sessionStorage`: recargar la página continúa la
 * sesión. No dice si la sesión está gastada (eso lo decide el servidor).
 */
const TOKEN_KEY = 'public-santa-chat:token'

export interface SessionTokenStore {
  subscribe(listener: () => void): () => void
  read(): string | null
  write(token: string | null): void
}

/**
 * Un almacén por isla. Con `sessionStorage` bloqueado (modo privado, política
 * del sitio) la isla sigue con el token en memoria: solo se pierde la
 * continuidad al recargar. No usa `getSessionItem` de `lib/pwa-storage`: esa
 * devuelve `null` también cuando el almacenamiento falla, y aquí hay que
 * distinguir «no hay token» de «no hay almacenamiento» para pasar a memoria.
 */
export function createSessionTokenStore(): SessionTokenStore {
  const listeners = new Set<() => void>()
  let inMemory: string | null = null
  let storageUsable = true
  const readStorage = (): string | null => {
    try {
      return window.sessionStorage.getItem(TOKEN_KEY)
    } catch {
      storageUsable = false
      return inMemory
    }
  }
  const writeStorage = (token: string | null) => {
    try {
      if (token) window.sessionStorage.setItem(TOKEN_KEY, token)
      else window.sessionStorage.removeItem(TOKEN_KEY)
    } catch {
      storageUsable = false
    }
  }
  return {
    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    read: () => (storageUsable ? readStorage() : inMemory),
    write(token) {
      inMemory = token
      writeStorage(token)
      listeners.forEach((listener) => listener())
    },
  }
}

/**
 * Marca del navegador (DD3): el día UTC en que este dispositivo abrió sesión.
 * Viaja como `deviceUsedOn` junto a la cookie httpOnly; el servidor aplica la
 * más restrictiva. La tarjeta de cierre la lee al volver el mismo día (04-02).
 * Sin `localStorage` (modo privado, bloqueado) la isla sigue: manda la cookie.
 */
export const deviceUsedOn = {
  /** Volver el mismo día sin sesión en curso: la isla abre directamente en la tarjeta. */
  usedTodayWithoutSession(sessionToken: SessionTokenStore): boolean {
    return deviceUsedOn.read() === utcDayOf(new Date()) && !sessionToken.read()
  },
  read: (): string | null => getStorageItem(STORAGE_KEYS.PUBLIC_CHAT_DEVICE_USED_ON),
  /** Sin almacenamiento no se marca: la cookie sigue marcando el dispositivo. */
  markToday() {
    setStorageItem(STORAGE_KEYS.PUBLIC_CHAT_DEVICE_USED_ON, utcDayOf(new Date()))
  },
}
